import {Box, Title} from '@mantine/core';
import type React from 'react';
import {COLOR_AI_BACKGROUND} from '../../../../config/constants/colorCodes';
import {ReportSummaryQuestionTypeID} from '../../../../core/entities/reportAnalytics/constants';
import type {
  parseReportSummary,
  summaryContent,
  summaryContentAdvice,
  summaryContentGeneral,
  summaryContentQuestion,
  summaryContentStep,
  summaryContentThemaTexts,
} from '../../../../core/repositories/reportSummaryRepository';
import {RegistBox} from '../../../../shared/components/RegistBox';
import {getAIGradient} from '../../usecase/getAIGradient';
import {
  AdviceBox,
  GeneralBox,
  QuestionBox,
  StepBox,
  ThemaTextsBox,
} from './SummaryContentBox';

type Props = {
  isSignedIn?: boolean;
  isPC: boolean;
  summaries?: parseReportSummary[];
  reportType?: string;
};

type ContentProps = {
  title: string;
  typeID: ReportSummaryQuestionTypeID;
  content: summaryContent | undefined;
  isPC: boolean;
  isSignedIn?: boolean;
  index: number;
  reportType?: string;
};

const ResultContentBox: React.FC<ContentProps> = ({
  title,
  typeID,
  content,
  isPC,
  index,
  reportType,
  isSignedIn = true,
}) => {
  const isSelection = reportType === 'selection';

  const isQuestionContent = (
    content: summaryContent | undefined,
  ): content is summaryContentQuestion => {
    return typeID === ReportSummaryQuestionTypeID.QUESTION;
  };

  const questionTitle =
    typeID === ReportSummaryQuestionTypeID.QUESTION &&
    isSelection &&
    isQuestionContent(content)
      ? content?.data[0]?.step
      : '';

  const renderSignedInContent = <T,>(
    Component: React.FC<{
      content: T;
      isPC: boolean;
      isSelection?: boolean;
      isSignedIn: boolean;
    }>,
    content: T,
  ) => {
    if (isSignedIn) {
      if (content && Array.isArray((content as any).data)) {
        return (
          <Component
            content={content}
            isPC={isPC}
            isSelection={isSelection}
            isSignedIn={isSignedIn}
          />
        );
      }

      return null;
    }

    return (
      <>
        <Component
          content={content}
          isPC={isPC}
          isSelection={isSelection}
          isSignedIn={isSignedIn}
        />
      </>
    );
  };

  const renderContent = () => {
    switch (typeID) {
      case ReportSummaryQuestionTypeID.STEP: {
        return content ? (
          <StepBox content={content as summaryContentStep} />
        ) : null;
      }

      case ReportSummaryQuestionTypeID.QUESTION: {
        return renderSignedInContent(
          QuestionBox,
          content as summaryContentQuestion,
        );
      }

      case ReportSummaryQuestionTypeID.ADVICE: {
        return renderSignedInContent(
          AdviceBox,
          content as summaryContentAdvice,
        );
      }

      case ReportSummaryQuestionTypeID.ES_QUESTION:
      case ReportSummaryQuestionTypeID.ES_POINT: {
        return renderSignedInContent(
          GeneralBox,
          content as summaryContentGeneral,
        );
      }

      case ReportSummaryQuestionTypeID.WEB_TEST:
      case ReportSummaryQuestionTypeID.GD:
      case ReportSummaryQuestionTypeID.OTHER: {
        return renderSignedInContent(
          ThemaTextsBox,
          content as summaryContentThemaTexts,
        );
      }

      case ReportSummaryQuestionTypeID.NULL: {
        return null;
      }
    }
  };

  return (
    <Box id={`summary-type-${index}`} className="w-full mb-20 p-4">
      <Box className="w-full flex mb-8">
        <Title
          order={2}
          style={{
            background: getAIGradient(),
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {questionTitle !== '' && questionTitle
            ? `${questionTitle}での質問内容`
            : title}
        </Title>
      </Box>
      <Box
        className="w-full text-black-1 rounded-12 p-12 relative"
        style={{backgroundColor: COLOR_AI_BACKGROUND}}
      >
        <Box className="p-8">
          <Box>{renderContent()}</Box>
        </Box>
        {!isSignedIn && typeID !== ReportSummaryQuestionTypeID.STEP && (
          <RegistBox />
        )}
      </Box>
    </Box>
  );
};

export const ResultSummaryBox: React.FC<Props> = ({
  isSignedIn,
  summaries,
  isPC,
  reportType,
}) => {
  return (
    <Box className="w-full flex items-center justify-center pc:p-24 p-12 bg-white">
      <Box className="w-full">
        {summaries?.map((summary, index) => (
          <ResultContentBox
            key={`summary-result-content-box-${index}`}
            typeID={summary.typeID}
            title={summary.typeName}
            content={summary.summary}
            isPC={isPC}
            isSignedIn={isSignedIn}
            index={index}
            reportType={reportType}
          />
        ))}
      </Box>
    </Box>
  );
};
